import React, { useState, useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getMyProfile, saveMyProfile } from '../../components/apiClient.js';
import ProfileNavigation from './ProfileNavigation';

interface Commitment {
  id: string;
  type: 'work' | 'school' | 'family' | 'sports' | 'other';
  hoursPerWeek: number;
}

interface CommitmentsData {
  commitments: Commitment[];
  totalAvailableHours: number;
  preferredStudyDays: string[];
  notes: string;
}

const Commitments: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Initialize React Hook Form with useFieldArray for dynamic commitments
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
    setValue
  } = useForm<CommitmentsData>({
    defaultValues: {
      commitments: [],
      totalAvailableHours: 40,
      preferredStudyDays: [],
      notes: ''
    },
    mode: 'onChange'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'commitments'
  });

  // Watch form values
  const formData = watch();

  // Load existing commitments from API
  useEffect(() => {
    const loadCommitments = async () => {
      try {
        const response = await getMyProfile();
        if (response.status === 'success' && response.result) {
          const profileData = response.result;
          setProfile(profileData);

          if (profileData.commitments) {
            // Parse commitments data if it exists
            const commitmentsData = typeof profileData.commitments === 'string'
              ? JSON.parse(profileData.commitments)
              : profileData.commitments;

            // Set form values from profile data
            setValue('commitments', commitmentsData.commitments || []);
            setValue('totalAvailableHours', commitmentsData.totalAvailableHours || 40);
            setValue('preferredStudyDays', commitmentsData.preferredStudyDays || []);
            setValue('notes', commitmentsData.notes || '');
          }
        }
      } catch (error) {
        console.error('Failed to load commitments:', error);
      }
    };

    loadCommitments();
  }, [setValue]);

  // Form submission handler
  const onSubmit = async (data: CommitmentsData) => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      console.log('Saving commitments:', data);

      // Merge form data with existing profile to prevent data loss
      const updatedProfile = {
        ...profile,
        commitments: data
      };

      const response = await saveMyProfile(updatedProfile);

      if (response.status === 'success') {
        setLastSaved(new Date());
        setProfile(updatedProfile);
      } else {
        throw new Error(response.message || 'Failed to save commitments');
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Add new commitment
  const addCommitment = () => {
    const newCommitment: Commitment = {
      id: Date.now().toString(),
      type: 'other',
      hoursPerWeek: 0
    };
    append(newCommitment);
  };

  const commitmentTypes = [
    { value: 'work', label: 'Work/Job', icon: 'fa-briefcase', color: 'primary' },
    { value: 'school', label: 'School/College', icon: 'fa-graduation-cap', color: 'success' },
    { value: 'family', label: 'Family', icon: 'fa-users', color: 'warning' },
    { value: 'sports', label: 'Sports/Fitness', icon: 'fa-running', color: 'info' },
    { value: 'other', label: 'Other', icon: 'fa-ellipsis-h', color: 'secondary' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'success' },
    { value: 'medium', label: 'Medium', color: 'warning' },
    { value: 'high', label: 'High', color: 'danger' }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  // Calculate total committed hours
  const totalCommittedHours = formData.commitments.reduce((sum, commitment) =>
    sum + (commitment.hoursPerWeek || 0), 0
  );

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-12">
            <ProfileNavigation className="mb-4" />
           
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h2 className="fw-bold mb-1">
                      <i className="fas fa-calendar-check me-2 text-primary"></i>
                      Commitments & Schedule
                    </h2>
                    <p className="text-muted mb-0">Manage your time commitments to optimize study planning</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/profile')}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Profile
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row g-4">
                    {/* Time Overview */}
                    <div className="col-12">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-info text-white">
                          <h5 className="mb-0">
                            <i className="fas fa-clock me-2"></i>
                            Time Overview
                          </h5>
                        </div>
                        <div className="card-body">
                          <div className="row g-3 text-center">
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded">
                                <h4 className="text-primary mb-0">{totalCommittedHours}h</h4>
                                <small className="text-muted">Committed per week</small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded">
                                <h4 className="text-success mb-0">{168 - totalCommittedHours}h</h4>
                                <small className="text-muted">Available per week</small>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <div className="bg-light p-3 rounded">
                                <h4 className="text-warning mb-0">{Math.round((totalCommittedHours / 168) * 100)}%</h4>
                                <small className="text-muted">Time utilization</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Commitments List */}
                    <div className="col-12">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            <i className="fas fa-list me-2"></i>
                            Your Commitments
                          </h5>
                          <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={addCommitment}
                          >
                            <i className="fas fa-plus me-1"></i>
                            Add Commitment
                          </button>
                        </div>
                        <div className="card-body">
                          {fields.length === 0 ? (
                            <div className="text-center py-4">
                              <i className="fas fa-calendar-plus fa-3x text-muted mb-3"></i>
                              <h5 className="text-muted">No commitments added yet</h5>
                              <p className="text-muted">Add your regular commitments to better plan your study schedule</p>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={addCommitment}
                              >
                                <i className="fas fa-plus me-2"></i>
                                Add Your First Commitment
                              </button>
                            </div>
                          ) : (
                            <div className="row g-3">
                              {fields.map((field, index) => (
                                <div key={field.id} className="col-12">
                                  <div className="card border-2 border-light">
                                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                      <h6 className="mb-0">Commitment #{index + 1}</h6>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => remove(index)}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                    <div className="card-body">
                                      <div className="row g-3">
                                        <div className="col-md-6">
                                          <Controller
                                            name={`commitments.${index}.type`}
                                            control={control}
                                            rules={{ required: 'Please select a type' }}
                                            render={({ field, fieldState }) => (
                                              <>
                                                <label className="form-label fw-medium">Type</label>
                                                <select
                                                  {...field}
                                                  className={`form-select ${fieldState.error ? 'is-invalid' : ''}`}
                                                >
                                                  {commitmentTypes.map(type => (
                                                    <option key={type.value} value={type.value}>
                                                      {type.label}
                                                    </option>
                                                  ))}
                                                </select>
                                                {fieldState.error && (
                                                  <div className="invalid-feedback">
                                                    {fieldState.error.message}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          />
                                        </div>
                                        
                                        
                                        <div className="col-md-6">
                                          <Controller
                                            name={`commitments.${index}.hoursPerWeek`}
                                            control={control}
                                            rules={{
                                              required: 'Please enter hours per week',
                                              min: { value: 0, message: 'Hours cannot be negative' }
                                            }}
                                            render={({ field, fieldState }) => (
                                              <>
                                                <label className="form-label fw-medium">Hours/Week</label>
                                                <input
                                                  {...field}
                                                  type="number"
                                                  className={`form-control ${fieldState.error ? 'is-invalid' : ''}`}
                                                  placeholder="0"
                                                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                                />
                                                {fieldState.error && (
                                                  <div className="invalid-feedback">
                                                    {fieldState.error.message}
                                                  </div>
                                                )}
                                              </>
                                            )}
                                          />
                                        </div>
                                        
                                        
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Preferred Study Days */}
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-success text-white">
                          <h5 className="mb-0">
                            <i className="fas fa-calendar-day me-2"></i>
                            Preferred Study Days
                          </h5>
                        </div>
                        <div className="card-body">
                          <Controller
                            name="preferredStudyDays"
                            control={control}
                            render={({ field }) => (
                              <div className="row g-2">
                                {daysOfWeek.map((day) => (
                                  <div key={day.value} className="col-12">
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={day.value}
                                        checked={field.value.includes(day.value)}
                                        onChange={(e) => {
                                          const updatedDays = e.target.checked
                                            ? [...field.value, day.value]
                                            : field.value.filter(d => d !== day.value);
                                          field.onChange(updatedDays);
                                        }}
                                      />
                                      <label className="form-check-label" htmlFor={day.value}>
                                        {day.label}
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="col-md-6">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-warning text-dark">
                          <h5 className="mb-0">
                            <i className="fas fa-sticky-note me-2"></i>
                            Notes
                          </h5>
                        </div>
                        <div className="card-body">
                          <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                              <textarea
                                {...field}
                                className="form-control"
                                rows={8}
                                placeholder="Add any additional notes about your schedule, preferences, or constraints..."
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                    <div className="d-flex align-items-center">
                      {lastSaved && (
                        <small className="text-muted">
                          <i className="fas fa-check text-success me-1"></i>
                          Last saved: {lastSaved.toLocaleTimeString()}
                        </small>
                      )}
                      {isDirty && !lastSaved && (
                        <small className="text-warning">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          You have unsaved changes
                        </small>
                      )}
                    </div>
                    <button
                      type="submit"
                      className={`btn btn-primary ${isSaving ? 'disabled' : !isValid ? 'disabled' : ''
                        }`}
                      disabled={isSaving || !isValid}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Commitments
                        </>
                      )}
                    </button>
                  </div>
                </form>
              
          </div>
        </div>
      </div>
    </div>
  );
};

export default Commitments;